'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { AnimatedGroup } from '@/components/ui/animated-group'
import { HeroHeader } from '@/components/header'
import Footer from '@/components/footer'
import { 
  BookOpen, 
  Calendar,
  Clock,
  ArrowRight,
  Search,
  Tag,
  TrendingUp,
  Users,
  Lightbulb,
  MessageSquare,
  Filter
} from 'lucide-react'
import Image from 'next/image'

const blogPosts = [
  {
    id: 1,
    title: "5 AI-Powered Networking Strategies That Actually Work",
    excerpt: "Discover how artificial intelligence is revolutionizing professional networking and learn practical strategies to leverage AI for meaningful connections.",
    author: "Favour",
    date: "2024-12-15",
    readTime: "8 min read",
    category: "AI & Technology",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=300&auto=format&fit=crop&q=60",
    bgColor: "bg-[#ffaf68]",
    rotateClass: "rotate-2",
    featured: true
  },
  {
    id: 2,
    title: "The Psychology of First Impressions in Virtual Networking",
    excerpt: "Understanding the science behind first impressions can transform your virtual networking game. Here's what research tells us.",
    author: "Favour",
    date: "2024-12-10",
    readTime: "6 min read",
    category: "Psychology",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=300&auto=format&fit=crop&q=60",
    bgColor: "bg-[#70b5ff]",
    rotateClass: "-rotate-1",
    featured: false
  },
  {
    id: 3,
    title: "From Introvert to Networking Pro: A Developer's Journey",
    excerpt: "How I transformed from dreading networking events to building meaningful professional relationships - and how you can too.",
    author: "Favour",
    date: "2024-12-05",
    readTime: "10 min read",
    category: "Personal Stories",
    image: "https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?w=600&h=300&auto=format&fit=crop&q=60",
    bgColor: "bg-[#79d45e]",
    rotateClass: "rotate-3",
    featured: true
  },
  {
    id: 4,
    title: "Building Authentic Connections in the Digital Age",
    excerpt: "In a world of LinkedIn requests and automated messages, here's how to create genuine professional relationships that last.",
    author: "Favour",
    date: "2024-11-28",
    readTime: "7 min read",
    category: "Networking Tips",
    image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&h=300&auto=format&fit=crop&q=60",
    bgColor: "bg-[#f4889a]",
    rotateClass: "-rotate-2",
    featured: false
  },
  {
    id: 5,
    title: "The Future of Event Technology: What's Coming Next",
    excerpt: "Exploring emerging technologies that will shape the future of professional events and networking experiences.",
    author: "Favour",
    date: "2024-11-20",
    readTime: "12 min read",
    category: "Future Tech",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=300&auto=format&fit=crop&q=60",
    bgColor: "bg-[#ba81ee]",
    rotateClass: "rotate-1",
    featured: false
  },
  {
    id: 6,
    title: "Why Most Networking Events Fail (And How to Fix Them)",
    excerpt: "An in-depth analysis of common networking event failures and practical solutions to create more engaging experiences.",
    author: "Favour",
    date: "2024-11-15",
    readTime: "9 min read",
    category: "Event Planning",
    image: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=600&h=300&auto=format&fit=crop&q=60",
    bgColor: "bg-[#4928fd]",
    rotateClass: "-rotate-3",
    featured: true
  }
]

const categories = [
  { name: "All", count: blogPosts.length },
  { name: "AI & Technology", count: blogPosts.filter(post => post.category === "AI & Technology").length },
  { name: "Networking Tips", count: blogPosts.filter(post => post.category === "Networking Tips").length },
  { name: "Psychology", count: blogPosts.filter(post => post.category === "Psychology").length },
  { name: "Personal Stories", count: blogPosts.filter(post => post.category === "Personal Stories").length },
  { name: "Future Tech", count: blogPosts.filter(post => post.category === "Future Tech").length },
  { name: "Event Planning", count: blogPosts.filter(post => post.category === "Event Planning").length }
]

const transitionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [searchQuery, setSearchQuery] = useState("")

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const featuredPosts = blogPosts.filter(post => post.featured)
  const regularPosts = filteredPosts.filter(post => !post.featured)

  return (
    <div className="w-full" style={{ backgroundColor: '#fdf1d3' }}>
      <HeroHeader />
      <main className="relative overflow-hidden">
        {/* Scattered decorative elements */}
        <div className="absolute top-20 left-10 w-8 h-8 bg-[#4928fd] border-4 border-black transform rotate-45 shadow-xl"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-[#ba81ee] border-4 border-black rounded-full shadow-xl"></div>
        <div className="absolute bottom-32 left-1/4 w-5 h-12 bg-[#79d45e] border-4 border-black transform -rotate-12 shadow-xl"></div>

        {/* Floating sticky note */}
        <div className="absolute top-32 right-4 bg-[#f6e683] border-4 border-black p-3 transform rotate-12 shadow-xl max-w-32 hidden md:block">
          <p className="text-xs font-black text-black">"Knowledge is power!"</p>
        </div>

        {/* Hero Section */}
        <section className="relative px-6 py-16 md:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <Badge variant="outline" className="border-white text-white bg-transparent">
                  <BookOpen className="mr-2 h-4 w-4" />
                  NETWORKING INSIGHTS
                </Badge>
              </div>
            </AnimatedGroup>

            <div className="bg-[#ffaf68] border-4 border-black p-8 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-black leading-tight">
                THE SOCIALLOOP<br />BLOG
              </h1>
            </div>

            <div className="bg-white border-4 border-black p-6 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-3xl">
              <p className="text-lg font-semibold text-black">
                Practical insights, proven strategies, and behind-the-scenes stories from the world of 
                professional networking. Learn how to build meaningful connections that advance your career.
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="relative px-6 py-8" style={{ backgroundColor: '#d9f3fc' }}>
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 lg:grid-cols-2 items-center">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-white border-4 border-black p-4 transform rotate-1 hover:rotate-0 transition-transform duration-200">
                  <div className="flex items-center gap-3">
                    <Search className="h-5 w-5 text-black" />
                    <Input
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="border-0 bg-transparent font-semibold placeholder:text-black/60 focus-visible:ring-0"
                    />
                  </div>
                </div>
              </AnimatedGroup>

              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.name}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`px-4 py-2 border-4 border-black font-black text-sm transform hover:rotate-0 transition-all duration-200 ${
                        selectedCategory === category.name
                          ? 'bg-[#79d45e] rotate-0'
                          : 'bg-white -rotate-1 hover:bg-gray-50'
                      }`}>
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </AnimatedGroup>
            </div>
          </div>
        </section>

        {/* Featured Posts */}
        {selectedCategory === "All" && (
          <section className="relative px-6 py-16">
            <div className="mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <AnimatedGroup variants={{ item: transitionVariants }}>
                  <div className="bg-[#79d45e] border-4 border-black p-3 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                    <Badge variant="outline" className="border-black text-black bg-transparent">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      FEATURED ARTICLES
                    </Badge>
                  </div>
                </AnimatedGroup>
                
                <div className="bg-[#70b5ff] border-4 border-black p-6 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <h2 className="text-3xl md:text-4xl font-black text-black">
                    MUST-READ INSIGHTS
                  </h2>
                </div>
              </div>

              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {featuredPosts.map((post, index) => (
                  <AnimatedGroup
                    key={post.id}
                    variants={{
                      item: {
                        ...transitionVariants,
                        visible: {
                          ...transitionVariants.visible,
                          transition: {
                            ...transitionVariants.visible.transition,
                            delay: index * 0.1
                          }
                        }
                      }
                    }}>
                    <article className={`${post.bgColor} border-4 border-black transform hover:rotate-0 transition-all duration-200 shadow-xl ${post.rotateClass} h-full`}>
                      <div className="relative">
                        <Image
                          src={post.image}
                          alt={post.title}
                          width={400}
                          height={200}
                          className="w-full h-48 object-cover border-b-4 border-black"
                        />
                        <div className="absolute top-4 left-4">
                          <div className="bg-white border-2 border-black px-2 py-1 text-xs font-black text-black">
                            FEATURED
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="bg-white border-2 border-black px-2 py-1 text-xs font-black text-black">
                            {post.category}
                          </div>
                          <div className="flex items-center text-xs font-semibold text-black/80">
                            <Clock className="h-3 w-3 mr-1" />
                            {post.readTime}
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-black text-black mb-3 line-clamp-2">
                          {post.title}
                        </h3>
                        
                        <p className="text-black font-semibold text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-white border-2 border-black rounded-full flex items-center justify-center">
                              <span className="text-xs font-black">F</span>
                            </div>
                            <div>
                              <p className="text-xs font-black text-black">{post.author}</p>
                              <p className="text-xs font-semibold text-black/70">{new Date(post.date).toLocaleDateString()}</p>
                            </div>
                          </div>
                          
                          <div className="bg-white border-2 border-black transform -rotate-2 hover:rotate-0 transition-transform duration-200">
                            <Button size="sm" className="bg-white hover:bg-gray-50 text-black border-0 font-black" asChild>
                              <Link href={`/blog/${post.id}`}>
                                Read More
                                <ArrowRight className="ml-1 h-3 w-3" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Decorative elements */}
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-white border-2 border-black transform rotate-45"></div>
                      <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-black rounded-full"></div>
                    </article>
                  </AnimatedGroup>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Posts */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#ba81ee' }}>
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-white border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200">
                  <h2 className="text-3xl md:text-4xl font-black text-black">
                    {selectedCategory === "All" ? "ALL ARTICLES" : selectedCategory.toUpperCase()}
                  </h2>
                </div>
              </AnimatedGroup>
            </div>

            {filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-white border-4 border-black p-8 inline-block transform rotate-1">
                  <p className="text-lg font-black text-black">No articles found matching your criteria.</p>
                  <Button 
                    onClick={() => {
                      setSelectedCategory("All")
                      setSearchQuery("")
                    }}
                    className="mt-4 bg-[#79d45e] hover:bg-[#79d45e]/90 text-black border-0 font-black">
                    Clear Filters
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {(selectedCategory === "All" ? regularPosts : filteredPosts).map((post, index) => (
                  <AnimatedGroup
                    key={post.id}
                    variants={{
                      item: {
                        ...transitionVariants,
                        visible: {
                          ...transitionVariants.visible,
                          transition: {
                            ...transitionVariants.visible.transition,
                            delay: index * 0.1
                          }
                        }
                      }
                    }}>
                    <article className={`${post.bgColor} border-4 border-black transform hover:rotate-0 transition-all duration-200 shadow-xl ${post.rotateClass} h-full`}>
                      <div className="relative">
                        <Image
                          src={post.image}
                          alt={post.title}
                          width={400}
                          height={200}
                          className="w-full h-48 object-cover border-b-4 border-black"
                        />
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="bg-white border-2 border-black px-2 py-1 text-xs font-black text-black">
                            {post.category}
                          </div>
                          <div className="flex items-center text-xs font-semibold text-black/80">
                            <Clock className="h-3 w-3 mr-1" />
                            {post.readTime}
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-black text-black mb-3 line-clamp-2">
                          {post.title}
                        </h3>
                        
                        <p className="text-black font-semibold text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-white border-2 border-black rounded-full flex items-center justify-center">
                              <span className="text-xs font-black">F</span>
                            </div>
                            <div>
                              <p className="text-xs font-black text-black">{post.author}</p>
                              <p className="text-xs font-semibold text-black/70">{new Date(post.date).toLocaleDateString()}</p>
                            </div>
                          </div>
                          
                          <div className="bg-white border-2 border-black transform -rotate-2 hover:rotate-0 transition-transform duration-200">
                            <Button size="sm" className="bg-white hover:bg-gray-50 text-black border-0 font-black" asChild>
                              <Link href={`/blog/${post.id}`}>
                                Read More
                                <ArrowRight className="ml-1 h-3 w-3" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Decorative elements */}
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-white border-2 border-black transform rotate-45"></div>
                      <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-black rounded-full"></div>
                    </article>
                  </AnimatedGroup>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <h2 className="text-3xl md:text-4xl font-black text-white">
                  Stay Updated!
                </h2>
              </div>
              <div className="bg-white border-4 border-black p-6 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-8 max-w-2xl">
                <p className="text-lg font-semibold text-black">
                  Get the latest networking insights, tips, and strategies delivered to your inbox. 
                  No spam, just valuable content to help you build better professional relationships.
                </p>
              </div>
              
              <div className="bg-[#79d45e] border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 max-w-lg">
                <div className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    className="border-4 border-black font-semibold bg-white"
                  />
                  <Button className="bg-[#4928fd] hover:bg-[#4928fd]/90 text-white border-4 border-black font-black whitespace-nowrap">
                    Subscribe
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </AnimatedGroup>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}